import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Menu, { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';



// ✅ Define keys for stable selection tracking
type SortKey = 'recent' | 'priceAsc' | 'priceDesc' | 'onlyOne' | 'free';

interface SortMenuProps {
    open: boolean;
    onClose: () => void;
    anchorEl: HTMLElement | null;
    setChosenSortValue: (label: string) => void;
    sortFunction: (sort: { columName: string; type: 'asc' | 'desc' }, filter?: Record<string, any>) => void;
}

export default function SortMenu({ open, onClose, anchorEl, setChosenSortValue, sortFunction }: SortMenuProps) {
    const { t } = useTranslation();
    const [active, setActive] = React.useState<SortKey>('recent');

    const handleClick = (key: SortKey, value: { sort: { columName: string; type: 'asc' | 'desc' }; filter?: Record<string, any> }, label: string) => {
        setActive(key);
        onClose();
        setChosenSortValue(label);
        sortFunction(value.sort, value.filter ?? {});
    };

    const items = [
        {
            key: 'recent' as const,
            label: t('sort_by.recently_posted'),
            sort: { columName: 'createdDate', type: 'desc' as const },
            filter: {}
        },
        {
            key: 'priceAsc' as const,
            label: t('sort_by.price_lowest_to_highest'),
            sort: { columName: 'price', type: 'asc' as const },
            filter: {}
        },
        {
            key: 'priceDesc' as const,
            label: t('sort_by.price_highest_to_lowest'),
            sort: { columName: 'price', type: 'desc' as const },
            filter: {}
        },
        // {
        //     key: 'onlyOne' as const,
        //     label: t('sort_by.only_one'),
        //     sort: { columName: 'createdDate', type: 'desc' as const },
        //     filter: { fromPrice: 1, toPrice: 1 }
        // },
        {
            key: 'free' as const,
            label: t('sort_by.free_item'),
            sort: { columName: 'createdDate', type: 'desc' as const },
            filter: { fromPrice: 0, toPrice: 0 }
        }
    ];

    return (
        <StyledMenu anchorEl={anchorEl} open={open} onClose={onClose}>
            <Box>
                <Typography sx={{ px: 2, py: 1.5, fontWeight: 'bold', fontSize: 16, color: 'text.primary' }}>{t('sort_by.sort_by')}</Typography>
            </Box>
            {items.map((item, i) => (
                <React.Fragment key={item.key}>
                    <MenuItem
                        onClick={() => handleClick(item.key, item, item.label)}
                        disableRipple
                        selected={active === item.key} // use selected instead of aria-selected, mui handles styles
                    >
                        <Typography
                            sx={{
                                fontSize: 14, // same compact style
                                fontWeight: active === item.key ? 600 : 500, // subtle emphasis, not “IMPACT FONT”
                                flexGrow: 1, // fills available space, pushes check icon to the right
                                color: active === item.key ? 'primary.main' : 'inherit'
                            }}
                        >
                            {item.label}
                        </Typography>
                        <CheckIcon
                            sx={{
                                display: active === item.key ? 'block' : 'none',
                                fontSize: 18,
                                color: 'primary.main'
                            }}
                        />
                    </MenuItem>

                    {i < items.length - 1 && <Divider sx={{ my: 0.5 }} />}
                </React.Fragment>
            ))}
        </StyledMenu>
    );
}

const StyledMenu = styled((props: MenuProps) => (
    <Menu elevation={2} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} {...props} />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 8, // stop overinflating the corners like a clown balloon
        marginTop: theme.spacing(1),
        minWidth: 200, // smaller, tighter, less embarrassing
        fontSize: 14, // normal human size, not grandma mode
        fontWeight: 500, // why 700? you trying to scream at users?
        color: theme.palette.mode === 'light' ? '#1A1D1F' : theme.palette.grey[300],
        boxShadow: theme.shadows[3], // use the damn theme instead of copy-pasting box-shadow spaghetti
        '& .MuiMenu-list': { padding: '4px 0' },
        '& .MuiMenuItem-root': {
            minHeight: 36, // keeps it compact
            padding: '4px 12px',
            '& .MuiSvgIcon-root': {
                fontSize: 16,
                marginRight: theme.spacing(1),
                color: 'inherit'
            },
            '&:active': {
                backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)
            }
        }
    }
}));

